import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn, dynStyle, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { checkbox, textbox } from "trader.ts/ui.common";
import { checkAllCasesHandled } from "trader.ts/utils";
import { match } from "ts-pattern";
import {
  enrollment_status,
  enrollmentid,
  formulaid,
  iban,
  instrumentid,
  internalteacherid,
  lessongroupid,
  lessonid,
  periodid,
  siteid,
  studentid,
  teacherid,
  thirdpartysellerid,
  weekday,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { renderBehindToggle } from "../../client/renderbehindtoggle";
import { renderSaveButton } from "../../client/savebutton";
import { applyPlaceholders } from "../../domain/emailtemplates/emailtemplates";
import { makeEmailWithNewTemplate } from "../../domain/emailtemplates/newemailstyles";
import { groupByIntoArrays } from "../../lib/groupbyintoarrays";
import { parseIntSafe } from "../../lib/parseintsafe";
import { allWeekdays } from "../../lib/time";
import { parseWeekday } from "../../parsing_from_ur";
import {
  HHmm,
  ddMMMMyyyy,
  renderEnrollmentStatus,
  renderFirstNameLastName,
  wrapInParenthesesIfNotEmpty,
} from "../../render";
import * as routes from "../../routes";
import { describePeriod, translateWeekday } from "../../translations_specific";
import { schoolurl } from "../../types";
import { stringToJsxClientside } from "../editemailtemplates/client";
import { renderEditor } from "./editor";
import { fileInputField, renderFileInput } from "../../client/fileinput";
import { arrayBufferToBase64Client } from "../../lib/arraybuffertobase64";

export type advancedStudentFilterValues = {
  internalteacherids: internalteacherid[];
  weekdays: joda.DayOfWeek[];
  instrumentids: instrumentid[];
  formulaids: formulaid[];
  periodids: periodid[];
  thirdpartysellerids: (thirdpartysellerid | null)[];
  siteids: (siteid | null)[];
  with_active_enrollment: with_active_enrollment;
  withunpaidinvoices: boolean;

  filter_on_age: boolean;
  min_age: number;
  max_age: number;
};

type with_active_enrollment = "no_filter" | "with_active_enrollment";

type studentFilterValuesS = {
  [k in keyof advancedStudentFilterValues]: Source<
    k extends "min_age" | "max_age" ? string : advancedStudentFilterValues[k]
  >;
};

export type advancedStudentFiltersData = {
  formulas: {
    id: formulaid;
    numberoflessons: number;
    lessonduration: number;
    startsfromperiod: boolean;
    description: string;
    comments: string;
    showonpublicplanning: boolean;
    type_: string;
    color: string;
    planningmode: string;
  }[];
  periods: {
    id: periodid;
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
    earliestpublicplanningdate: joda.LocalDate;
    openpublicenrollments: joda.LocalDate | null;
    closepublicenrollments: joda.LocalDate;
  }[];
  instruments: {
    id: instrumentid;
    description: string;
    showonpublicplanning: boolean;
  }[];
  internalteachers: {
    internalteacherid: internalteacherid;
    id: teacherid;
    firstname: string;
    lastname: string;
  }[];
  // schoolname: string;
  // schoolurl: schoolurl;
  third_party_sellers: {
    id: thirdpartysellerid | null;
    name: string;
  }[];
  sites: {
    description: string;
    id: siteid | null;
  }[];
};

export type filteredStudent = {
  selectedS: Source<boolean>;

  lessongroup_id: lessongroupid;
  lessongroup_time: joda.LocalTime;
  lessongroup_weekday: weekday;
  lessongroup_lastlesson: joda.LocalDate | null;
  lessongroup_startsinperiod: periodid;
  lessongroup_duration: number;
  lessongroup_numberofplannedlessons: number;
  lessongroup_formuladescription: string;
  room_description: string | null;
  site_description: string | null;
  teacher_firstname: string;
  teacher_lastname: string;
  teacher_phone: string;
  teacher_email: string;
  teacher_id: teacherid;
  enrollment_id: enrollmentid;
  enrollment_created: joda.LocalDate;
  enrollment_status: enrollment_status;
  enrollment_startedlessonson: joda.LocalDate;
  enrollment_stoppedlessonson: joda.LocalDate | null;
  third_party_seller_name: string | null;
  student_firstname: string;
  student_lastname: string;
  student_id: studentid;
  student_street: string;
  student_number: string;
  student_bus: string;
  student_zip: string;
  student_city: string;
  student_comments: string;
  student_birthday: joda.LocalDate | null;
  student_iban: iban | null;
  student_name_on_iban: string;
  lessons:
    | {
        id: lessonid;
        date: joda.LocalDate;
      }[]
    | null;
  student_phone_numbers:
    | {
        number: string;
        description: string | null;
      }[]
    | null;
  student_emails:
    | {
        email: string;
      }[]
    | null;
  instrument_description: string;
};

const withActiveEnrollmentValues = [
  "with_active_enrollment" as const,
  "no_filter" as const,
];

export function render(opts: {
  schoolname: string;
  schoolurl: schoolurl;
  filters: advancedStudentFiltersData;
}): HTMLElement | HTMLElement[] {
  const filters = opts.filters;
  const [renderedFilterSidebar, filterValuesS] =
    renderAdvancedFilterStudentsSidebar(filters);

  const { filteredStudentsS, loadingS } = makeAdvancedFilteredStudentSources(
    filterValuesS,
    filters,
  );

  const modeS: Source<"filter" | "message"> = new Source("filter");
  const titleS = new Source("");
  const htmlS = new Source("");

  const attachmentS: Source<{
    data: ArrayBuffer;
    mimetype: string;
    name: string;
  } | null> = new Source(null);

  const collapseStudentsS = new Source(true);

  return dyn(modeS, (mode) => {
    if (mode === "filter") {
      return [
        renderedFilterSidebar,

        <main>
          <label>{"Toegepaste filters"}</label>
          <section className={style.grid_card_small}>
            {renderAdvancedFiltersRecap(filters, filterValuesS)}
          </section>

          <label>{"Verder"}</label>
          <section>
            <button
              style="width: 100%"
              className={style.button_accent}
              onclick={() => modeS.set("message")}
            >
              {"Schrijf bericht"}
              <i
                className={FA.icon_arrow_right}
                style="margin-left: 24px; margin-right: 0px"
              ></i>
            </button>
          </section>

          <label>{"Studenten"}</label>
          <section className={style.panel}>
            {dyn(filteredStudentsS, function (filteredStudents) {
              const studentRows = renderStudentRows(filteredStudents);
              return (
                <div>
                  {dynVariadic(
                    filteredStudents.map((s) => s.selectedS),
                    (selected) => {
                      const numberOfLgs = filteredStudents
                        .map((s) => s.lessongroup_id)
                        .uniq().length;
                      const numberOfStudents = selected
                        .map((s, i) =>
                          s === true ? filteredStudents[i].student_id : null,
                        )
                        .compact()
                        .uniq().length;
                      return (
                        <article style="display: flex">
                          {dyn(loadingS, (loading) =>
                            loading ? (
                              <div className={style.loading}></div>
                            ) : (
                              <div>
                                <div style="font-weight: bold; margin-bottom: 16px; flex: 1">
                                  {"We vonden %lessons lessen voor je filters. Je zal %students studenten een bericht sturen."
                                    .replace("%lessons", numberOfLgs.toString())
                                    .replace(
                                      "%students",
                                      numberOfStudents.toString(),
                                    )}
                                </div>
                                <div>
                                  {renderCollapseButton(collapseStudentsS)}
                                </div>
                              </div>
                            ),
                          )}
                        </article>
                      );
                    },
                  )}

                  {dynStyle(
                    collapseStudentsS,
                    (collapsed) => (collapsed ? "display: none" : ""),
                    <div>
                      {dyn(
                        filterValuesS.with_active_enrollment,
                        (with_active_enrollment) => {
                          return renderFilteringButtons(filteredStudents, {
                            with_active_enrollment,
                          });
                        },
                      )}

                      {studentRows}
                    </div>,
                  )}
                </div>
              );
            })}
          </section>
        </main>,
      ];
    } else if (mode === "message") {
      const filteredSelectedStudents = filteredStudentsS
        .get()
        .filter((s) => s.selectedS.get() === true);

      const variables = [
        {
          technicalname: "student_firstname",
          description: "Voornaam student",
          extract: (s: filteredStudent) => s.student_firstname,
        },
        {
          technicalname: "student_lastname",
          description: "Achternaam student",
          extract: (s: filteredStudent) => s.student_lastname,
        },
        {
          technicalname: "teacher_firstname",
          description: "Voornaam docent",
          extract: (s: filteredStudent) => s.teacher_firstname,
        },
        {
          technicalname: "teacher_lastname",
          description: "Achternaam docent",
          extract: (s: filteredStudent) => s.teacher_lastname,
        },
        {
          technicalname: "teacher_phone",
          description: "Telefoon docent",
          extract: (s: filteredStudent) => s.teacher_phone,
        },
        {
          technicalname: "teacher_email",
          description: "Email docent",
          extract: (s: filteredStudent) => s.teacher_email,
        },
        {
          technicalname: "instrument_description",
          description: "Cursus",
          extract: (s: filteredStudent) => s.instrument_description,
        },
        {
          technicalname: "time",
          description: "Tijdstip start les",
          extract: (s: filteredStudent) => s.lessongroup_time.format(HHmm),
        },
        {
          technicalname: "endtime",
          description: "Tijdstip einde les",
          extract: (s: filteredStudent) =>
            s.lessongroup_time.plusMinutes(s.lessongroup_duration).format(HHmm),
        },
        {
          technicalname: "duration",
          description: "Aantal minuten les",
          extract: (s: filteredStudent) => s.lessongroup_duration.toString(),
        },
        {
          technicalname: "weekday",
          description: "Weekdag",
          extract: (s: filteredStudent) =>
            translateWeekday(parseWeekday(s.lessongroup_weekday)),
        },
        {
          technicalname: "lessongroup_firstlesson",
          description: "Datum eerste les",
          extract: (s: filteredStudent) =>
            ddMMMMyyyy(s.enrollment_startedlessonson),
        },
        {
          technicalname: "room",
          description: "Lokaal",
          extract: (s: filteredStudent) => s.room_description || "",
        },
        {
          technicalname: "site",
          description: "Site",
          extract: (s: filteredStudent) => s.site_description || "",
        },
      ];

      const editor = renderEditor(htmlS, null, {
        variables,
        records: filteredSelectedStudents,
      });

      return (
        <main>
          <div className={style.card + style.card_large}>
            {textbox({ source: titleS, label: "Onderwerp" })}

            <label>{"Bericht"}</label>
            {editor.buttonSection}
            {editor.editablediv}
            <label for="">
              {"Bijlage"}
              <div className={style.input_with_icon}>
                <i className={FA.icon_paperclip_solid}></i>
                {renderFileInput({
                  source: attachmentS,
                  initiallabel: "Voeg bijlage toe",
                  selectedlabel: "Bijlage toegevoegd",
                  className: style.input_like,
                  style: "cursor: pointer",
                })}
              </div>
            </label>
          </div>
          {dynVariadic([titleS, htmlS], ([title, html]) => {
            const firstStu = filteredSelectedStudents[0];
            const variablesAsObj = Object.fromEntries(
              variables.map((variab) => [
                variab.technicalname,
                firstStu ? variab.extract(firstStu) : "",
              ]),
            );

            const usedPlaceholders = [
              ...(html.matchAll(/{{(\S+?)}}/g) || []),
              ...(title.matchAll(/{{(\S+?)}}/g) || []),
            ]
              .compact()
              .uniq();
            const containsPlaceHolders = usedPlaceholders.length > 0;

            const replaced = applyPlaceholders(
              { placeholders: {}, placeholder_help: {} },
              { title, html },
              variablesAsObj,
            );
            const exampleTitle = (
              <div className={style.input_like}>{replaced.title}</div>
            );
            const exampleBody = (() => {
              const container = <div></div>;
              const shadow = container.attachShadow({ mode: "closed" });

              const parsed = makeEmailWithNewTemplate(
                { schoolname: opts.schoolname, schoolurl: opts.schoolurl },
                stringToJsxClientside(replaced.body),
              );

              Array.from(parsed.getElementsByTagName("style")).forEach((st) => {
                shadow.appendChild(st);
              });
              shadow.appendChild(parsed.getElementsByTagName("body")[0]);
              return container;
            })();

            async function doSend() {
              const title = titleS.get();
              const html = htmlS.get();
              if (title.trim() === "") {
                alert("Vul het onderwerp in");
                return;
              }
              if (html.trim() === "") {
                alert("Vul je email in");
                return;
              }
              const attachmentCurr = attachmentS.get();
              const attachment = attachmentCurr
                ? {
                    data_b64: arrayBufferToBase64Client(attachmentCurr.data),
                    mimetype: attachmentCurr.mimetype,
                    name: attachmentCurr.name,
                  }
                : null;
              await rpc(routes.sendGroupMessageSpec, undefined, {
                html,
                title,
                attachment,
                target: containsPlaceHolders
                  ? {
                      tag: "enrollments",
                      enrollments: filteredSelectedStudents
                        .map((s) => {
                          return {
                            id: s.enrollment_id,
                            studentid: s.student_id,
                            variables: Object.fromEntries(
                              [...usedPlaceholders]
                                .map((ph): [string, string] | null => {
                                  const variab = variables.find(
                                    (vari) => vari.technicalname === ph[1],
                                  );
                                  return variab
                                    ? [
                                        variab.technicalname,
                                        variab ? variab.extract(s) : "",
                                      ]
                                    : null;
                                })
                                .compact(),
                            ),
                          };
                        })
                        .uniq(),
                    }
                  : {
                      tag: "students",
                      studentids: filteredSelectedStudents
                        .map((s) => s.student_id)
                        .uniq(),
                    },
              });
            }

            return [
              <section
                className={style.card + style.card_large}
                style="width: 100%;"
              >
                <div>
                  <label for="">{"Voorbeeld titel"}</label>
                  {exampleTitle}
                </div>

                <div>
                  <label for="">{"Voorbeeld bericht"}</label>
                  {exampleBody}
                </div>
              </section>,

              <section
                className={style.card + style.card_large}
                style="width: 100%;"
              >
                <article>
                  {!containsPlaceHolders
                    ? "Je hebt %i studenten geselecteerd die je bericht zullen krijgen.".replace(
                        "%i",
                        filteredSelectedStudents
                          .map((s) => s.student_id)
                          .uniq()
                          .length.toString(),
                      )
                    : "Je hebt variabelen in je bericht toegevoegd, dus zal classy een bericht sturen voor elke geselecteerde inschrijving. Je hebt %i inschrijvingen geselecteerd.".replace(
                        "%i",
                        filteredSelectedStudents.length.toString(),
                      )}
                </article>

                <div style="text-align: right">
                  <button onclick={() => modeS.set("filter")}>
                    <div className={style.text_with_icon}>
                      <i className={FA.icon_arrow_left}></i>
                      <div>{"Bekijk ze hier"}</div>
                    </div>
                  </button>
                  {renderSaveButton({
                    save: doSend,
                    text: {
                      normal: "Verzend berichten",
                      success: "Berichten verzonden!",
                    },
                    reload: false,
                    timeout: 24 * 60 * 60 * 1000,
                  })}
                </div>
              </section>,
            ];
          })}
        </main>
      );
    } else {
      return checkAllCasesHandled(mode);
    }
  });
}

export function makeAdvancedFilteredStudentSources(
  filterValuesS: studentFilterValuesS,
  filters: advancedStudentFiltersData,
) {
  scheduleForCleanup(filterValuesS.internalteacherids.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.weekdays.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.instrumentids.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.formulaids.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.periodids.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.withunpaidinvoices.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.thirdpartysellerids.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.siteids.observe(recalcTargets));
  scheduleForCleanup(
    filterValuesS.with_active_enrollment.observe(recalcTargets),
  );
  scheduleForCleanup(filterValuesS.filter_on_age.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.min_age.observe(recalcTargets));
  scheduleForCleanup(filterValuesS.max_age.observe(recalcTargets));

  const filteredStudentsS: Source<filteredStudent[]> = new Source([]);
  const loadingS = new Source(false);
  void recalcTargets();

  const today = joda.LocalDate.now(); // no tz, but accurate enough

  async function recalcTargets() {
    loadingS.set(true);
    const values_init: advancedStudentFilterValues = {
      internalteacherids: filterValuesS.internalteacherids.get(),
      weekdays: filterValuesS.weekdays.get(),
      instrumentids: filterValuesS.instrumentids.get(),
      formulaids: filterValuesS.formulaids.get(),
      periodids: filterValuesS.periodids.get(),
      withunpaidinvoices: filterValuesS.withunpaidinvoices.get(),
      thirdpartysellerids: filterValuesS.thirdpartysellerids.get(),
      siteids: filterValuesS.siteids.get(),
      with_active_enrollment: filterValuesS.with_active_enrollment.get(),
      filter_on_age: filterValuesS.filter_on_age.get(),
      min_age: parseIntSafe(filterValuesS.min_age.get()) ?? 0,
      max_age: parseIntSafe(filterValuesS.max_age.get()) ?? 99,
    };
    const values: advancedStudentFilterValues = {
      internalteacherids:
        values_init.internalteacherids.length > 0
          ? values_init.internalteacherids
          : filters.internalteachers.map((i) => i.internalteacherid),
      weekdays:
        values_init.weekdays.length > 0 ? values_init.weekdays : allWeekdays,
      instrumentids:
        values_init.instrumentids.length > 0
          ? values_init.instrumentids
          : filters.instruments.map((i) => i.id),
      formulaids:
        values_init.formulaids.length > 0
          ? values_init.formulaids
          : filters.formulas.map((i) => i.id),
      periodids:
        values_init.periodids.length > 0
          ? values_init.periodids
          : filters.periods.map((i) => i.id),
      withunpaidinvoices: filterValuesS.withunpaidinvoices.get(),
      thirdpartysellerids:
        values_init.thirdpartysellerids.length > 0
          ? values_init.thirdpartysellerids
          : filters.third_party_sellers.map((i) => i.id),
      siteids:
        values_init.siteids.length > 0
          ? values_init.siteids
          : filters.sites.map((i) => i.id),
      with_active_enrollment: values_init.with_active_enrollment,
      filter_on_age: values_init.filter_on_age,
      min_age: values_init.min_age,
      max_age: values_init.max_age,
    };
    const students = await rpc(
      routes.getFilteredStudentsSpec,
      undefined,
      values,
    );
    const withSelected: filteredStudent[] = students.map((s) => ({
      ...s,
      selectedS: new Source(enrollmentIsActive(today, s)),
    }));

    filteredStudentsS.set(
      withSelected.filter(
        (s) =>
          // Removing students for which we have another entry, that is selected by default
          !(
            s.selectedS.get() === false &&
            withSelected.some(
              (s2) =>
                s2.student_id === s.student_id && s2.selectedS.get() === true,
            )
          ),
      ),
    );
    loadingS.set(false);
  }
  return { filteredStudentsS, loadingS };
}

function renderCheckboxes<Item, Id>(
  allChoices: Item[],
  idsS: Source<Id[]>,
  toId: (item: Item) => Id,
  show: (item: Item) => string,
): HTMLElement {
  function handleClickNoFilter() {
    // Clicking on "Geen filter" always removes all selections
    idsS.set([]);
  }

  function handleClick(id: Id) {
    if (idsS.get().includes(id)) {
      idsS.set(
        idsS.get().filter(
          (i) =>
            // eslint-disable-next-line
            i !== id,
        ),
      );
    } else {
      idsS.set(idsS.get().concat([id]));
    }
  }

  return (
    <div>
      {allChoices.map((choice) => {
        return (
          <label className={style.checkbox}>
            {dyn(idsS, (ids) => (
              <input
                type="checkbox"
                checked={ids.includes(toId(choice)) ? "true" : null}
                onclick={() => handleClick(toId(choice))}
              />
            ))}
            {show(choice)}
          </label>
        );
      })}
      <label className={style.checkbox}>
        {dyn(idsS, (ids) => (
          <input
            type="checkbox"
            checked={ids.length === 0 ? "true" : null}
            onclick={handleClickNoFilter}
          />
        ))}
        {"Geen filter"}
      </label>
    </div>
  );
}

export function renderAdvancedFilterStudentsSidebar(
  filters: advancedStudentFiltersData,
): [HTMLElement, studentFilterValuesS] {
  // no tz, but accurate enough
  const today = joda.LocalDate.now();

  const filterTogglesS = {
    internalteacherids: new Source(true),
    weekdays: new Source(false),
    instrumentids: new Source(false),
    formulaids: new Source(false),
    periodids: new Source(true),
    withunpaidinvoices: new Source(false),
    thirdpartysellerids: new Source(false),
    siteids: new Source(false),
    with_active_enrollment: new Source(false),
    age: new Source(false),
  };

  const filterValuesS: studentFilterValuesS = {
    internalteacherids: new Source([] as internalteacherid[]),
    weekdays: new Source([] as joda.DayOfWeek[]),
    instrumentids: new Source([] as instrumentid[]),
    formulaids: new Source([] as formulaid[]),
    /* By default, we select every period that is in progress or in the future  */
    periodids: new Source(
      filters.periods.filter((p) => p.lastday.isAfter(today)).map((p) => p.id),
    ),
    withunpaidinvoices: new Source(false),
    thirdpartysellerids: new Source([] as thirdpartysellerid[]),
    siteids: new Source([] as siteid[]),
    with_active_enrollment: new Source("no_filter"),
    filter_on_age: new Source(false),
    min_age: new Source("0"),
    max_age: new Source("99"),
  };

  return [
    <aside>
      <div>
        <b>{"Filters"}</b>
      </div>

      {filters.internalteachers.length > 1 ? (
        renderBehindToggle(
          filterTogglesS.internalteacherids,
          "Docent",
          renderCheckboxes(
            filters.internalteachers,
            filterValuesS.internalteacherids,
            (i) => i.internalteacherid,
            renderFirstNameLastName,
          ),
        )
      ) : (
        <span></span>
      )}

      {renderBehindToggle(
        filterTogglesS.weekdays,
        "Weekdag",
        renderCheckboxes(
          allWeekdays,
          filterValuesS.weekdays,
          (i) => i,
          translateWeekday,
        ),
      )}

      {renderBehindToggle(
        filterTogglesS.instrumentids,
        "Cursus",
        renderCheckboxes(
          filters.instruments,
          filterValuesS.instrumentids,
          (i) => i.id,
          (i) => i.description,
        ),
      )}

      {renderBehindToggle(
        filterTogglesS.formulaids,
        "Formule",
        renderCheckboxes(
          filters.formulas,
          filterValuesS.formulaids,
          (i) => i.id,
          (i) => i.description,
        ),
      )}

      {filters.third_party_sellers.length > 1
        ? renderBehindToggle(
            filterTogglesS.thirdpartysellerids,
            "Vereniging",
            renderCheckboxes(
              filters.third_party_sellers,
              filterValuesS.thirdpartysellerids,
              (i) => i.id,
              (i) => i.name,
            ),
          )
        : []}

      {filters.sites.length > 1
        ? renderBehindToggle(
            filterTogglesS.siteids,
            "Vestiging",
            renderCheckboxes(
              filters.sites,
              filterValuesS.siteids,
              (i) => i.id,
              (i) => i.description,
            ),
          )
        : []}

      {renderBehindToggle(
        filterTogglesS.periodids,
        "Ingeschreven in jaar",
        renderCheckboxes(
          filters.periods,
          filterValuesS.periodids,
          (i) => i.id,
          (i) => describePeriod(i.firstday, i.lastday),
        ),
      )}

      {renderBehindToggle(
        filterTogglesS.withunpaidinvoices,
        "Nog te betalen",
        <div>
          {renderRadioCheckboxes(
            [true, false],
            filterValuesS.withunpaidinvoices,
            (s) => s,
            (s) =>
              s === true
                ? "Heeft onbetaalde Factuur"
                : s === false
                ? "Geen filter"
                : checkAllCasesHandled(s),
          )}
        </div>,
      )}

      {renderBehindToggle(
        filterTogglesS.with_active_enrollment,
        "Met actieve inschrijving",
        renderRadioCheckboxes(
          withActiveEnrollmentValues,
          filterValuesS.with_active_enrollment,
          (i) => i,
          renderWithActiveEnrollment,
        ),
      )}

      {renderBehindToggle(
        filterValuesS.filter_on_age,
        "Leeftijd",
        <div className={style.equal_columns}>
          {textbox({ source: filterValuesS.min_age, type: "number" })}
          {textbox({ source: filterValuesS.max_age, type: "number" })}
        </div>,
      )}
    </aside>,
    filterValuesS,
  ];
}

const recapStyle = [
  "max-width: 250px",
  "align-self: flex-start", // So small items on the row don't stetch to the size of the big items
].join(";");

export function renderAdvancedFiltersRecap(
  filters: advancedStudentFiltersData,
  filterValuesS: studentFilterValuesS,
): HTMLElement | HTMLElement[] {
  const teacherRecap = renderRecap(
    "Docent",
    filterValuesS.internalteacherids,
    filters.internalteachers,
    (i) => i.internalteacherid,
    (i) => i.firstname + " " + i.lastname,
  );

  const weekdaysRecap = renderRecap(
    "Weekdag",
    filterValuesS.weekdays,
    allWeekdays,
    (i) => i,
    translateWeekday,
  );

  const instrumentsRecap = renderRecap(
    "Cursus",
    filterValuesS.instrumentids,
    filters.instruments,
    (i) => i.id,
    (i) => i.description,
  );

  const formulasRecap = renderRecap(
    "Lesformule",
    filterValuesS.formulaids,
    filters.formulas,
    (i) => i.id,
    (i) => i.description,
  );

  const thirdpartysellersRecap = renderRecap(
    "Vestiging",
    filterValuesS.thirdpartysellerids,
    filters.third_party_sellers,
    (i) => i.id,
    (i) => i.name,
  );

  const periodsRecap = renderRecap(
    "Ingeschreven in jaar",
    filterValuesS.periodids,
    filters.periods,
    (i) => i.id,
    (i) => describePeriod(i.firstday, i.lastday),
  );

  const sitesRecap = renderRecap(
    "Site",
    filterValuesS.siteids,
    filters.sites,
    (i) => i.id,
    (i) => i.description,
  );

  const withActiveEnrollmentRecap = renderRadioRecap(
    withActiveEnrollmentValues,
    filterValuesS.with_active_enrollment,
    (i) => i,
    renderWithActiveEnrollment,
    { title: "Met actieve inschrijving", noFilterValue: "no_filter" },
  );

  const ageRecap = dynVariadic(
    [filterValuesS.filter_on_age, filterValuesS.min_age, filterValuesS.max_age],
    ([filter_on_age, min, max]) => {
      if (filter_on_age === false) {
        return <span style="display: none;"></span>;
      } else {
        return (
          <div className={style.card} style={recapStyle}>
            <div>
              <label>{"Leeftijd"}</label>
              <div>
                {"%from tot %to"
                  .replace("%from", (parseIntSafe(min) ?? 0).toString())
                  .replace("%to", (parseIntSafe(max) ?? 0).toString())}
              </div>
            </div>
          </div>
        );
      }
    },
  );

  const invoicesRecap = renderRadioRecap(
    [false, true],
    filterValuesS.withunpaidinvoices,
    (s) => s,
    (s) => (s === true ? "Heeft onbetaalde factuur" : ""),
    { noFilterValue: false, title: "Nog te betalen" },
  );

  return [
    ...teacherRecap,
    ...weekdaysRecap,
    ...instrumentsRecap,
    ...formulasRecap,
    ...thirdpartysellersRecap,
    ...periodsRecap,
    ...invoicesRecap,
    ...sitesRecap,
    ...withActiveEnrollmentRecap,
    ...ageRecap,
  ];
}

function renderRecap<Item, Id>(
  title: string,
  idsS: Source<Id[]>,
  items: Item[],
  toId: (item: Item) => Id,
  show: (item: Item) => string,
) {
  return dyn(idsS, (ids) => {
    if (ids.length === 0) {
      return <span style="display: none"></span>;
    } else {
      const selectedItems = items.filter((item) => ids.includes(toId(item)));
      return (
        <div className={style.card} style={recapStyle}>
          <div>
            <label>{title}</label>
            {selectedItems.map((item) => (
              <div>{show(item)}</div>
            ))}
          </div>
        </div>
      );
    }
  });
}

function renderFilteringButtons(
  filteredStudents: filteredStudent[],
  opts: { with_active_enrollment: with_active_enrollment },
) {
  const today = joda.LocalDate.now();
  const customStyle = `
    display: flex;
    margin: 16px -16px 0px;
    padding: 16px 32px;
    background-color: var(--grey-100);
  `;
  return (
    <div style={customStyle}>
      {opts.with_active_enrollment !== "no_filter" ? (
        <div style="flex: 1"></div>
      ) : (
        []
      )}

      <button
        onclick={() => filteredStudents.forEach((s) => s.selectedS.set(true))}
      >
        {"Selecteer iedereen"}
      </button>

      {opts.with_active_enrollment === "no_filter" ? (
        <button
          style="flex: 1"
          onclick={() =>
            filteredStudents.forEach((s) => {
              const willSelect = enrollmentIsActive(today, s);
              s.selectedS.set(willSelect);
            })
          }
        >
          {"Selecteer iedereen met een actieve inschrijving"}
        </button>
      ) : (
        []
      )}

      <button
        className={style.button_em_down}
        onclick={() =>
          filteredStudents.forEach((s) => {
            s.selectedS.set(false);
          })
        }
      >
        {"Selecteer niemand"}
      </button>
    </div>
  );
}

function renderStudentRows(filteredStudents: filteredStudent[]) {
  const timeFormat = joda.DateTimeFormatter.ofPattern("HH:mm");

  const tdStyle = "border: 0px; padding: 0px 8px;";
  const tdStyleFirstOfTeacher = "padding-top: 32px;";

  const rows = groupByIntoArrays(
    filteredStudents,
    (s) => s.teacher_firstname + " " + s.teacher_lastname,
  )
    .orderBy(([teachername]) => teachername)
    .map(([teachername, studentsByTeacher]) => {
      const studentsPerTeacherRendered = groupByIntoArrays(
        studentsByTeacher,
        (s) => s.lessongroup_id,
      )
        .orderBy(([_, studentsByLg]) =>
          studentsByLg[0].lessongroup_time.toSecondOfDay(),
        )
        .orderBy(([_, studentsByLg]) =>
          parseWeekday(studentsByLg[0].lessongroup_weekday).ordinal(),
        )
        .map(([_lessongroupid, studentsByLg], byTeacherIndex) => {
          const studentsByLgRendered = studentsByLg
            .orderBy((stu) => stu.student_firstname)
            .map((student, byLgIndex) => {
              const isFirstForTeacher = byTeacherIndex === 0 && byLgIndex === 0;
              const tdStyleThisRow =
                tdStyle + (isFirstForTeacher ? tdStyleFirstOfTeacher : "");
              return (
                <tr>
                  <td style={tdStyleThisRow}>
                    {isFirstForTeacher ? teachername : ""}
                  </td>
                  <td style={tdStyleThisRow}>
                    {byLgIndex === 0
                      ? translateWeekday(
                          parseWeekday(student.lessongroup_weekday),
                        ) +
                        " - " +
                        student.lessongroup_time.format(timeFormat)
                      : ""}
                  </td>
                  <td style={tdStyleThisRow}>
                    {checkbox({
                      source: student.selectedS,
                      label:
                        `${student.student_firstname} ${student.student_lastname}` +
                        wrapInParenthesesIfNotEmpty(
                          renderEnrollmentStatus(student.enrollment_status, {
                            startedlessonson:
                              student.enrollment_startedlessonson,
                            stoppedlessonson:
                              student.enrollment_stoppedlessonson,
                            lessons: {
                              tag: "show_last_lesson",
                              lessons: student.lessons || [],
                            },
                          }),
                        ),
                    })}
                  </td>
                </tr>
              );
            });
          return studentsByLgRendered;
        });
      return studentsPerTeacherRendered;
    });

  // Offsetting first row of teacher
  const headerStyle = "padding: 0px 8px; position: relative; top: 32px";
  return (
    <table style="">
      <tr>
        <th style={headerStyle}>{"Docent"}</th>
        <th style={headerStyle}>{"Les"}</th>
        <th style={headerStyle}>{"Student"}</th>
      </tr>

      {rows}
    </table>
  );
}

function renderCollapseButton(collapseStudentsS: Source<boolean>) {
  return dyn(collapseStudentsS, function (collapse) {
    if (collapse === true) {
      return (
        <button onclick={() => collapseStudentsS.set(false)}>
          {"Toon"}
        </button>
      );
    } else {
      return (
        <button onclick={() => collapseStudentsS.set(true)}>
          {"Verberg"}
        </button>
      );
    }
  });
}

export function renderTeachers(opts: {
  teachers: {
    id: teacherid;
    firstname: string;
    lastname: string;
    email: string;
    internalteacherid: internalteacherid;
    instruments:
      | {
          id: instrumentid;
          description: string;
        }[]
      | null;
  }[];
}): HTMLElement | HTMLElement[] {
  const modeS: Source<"filter" | "message"> = new Source("filter");
  const instrumentFilterToggleS = new Source(true);
  const instrumentsS = new Source([] as instrumentid[]);
  const instruments = opts.teachers
    .flatMap((t) => t.instruments || [])
    .uniqBy((instr) => instr.id)
    .orderBy((instr) => instr.description);

  const titleS = new Source("");
  const htmlS = new Source("");

  const attachmentS: Source<{
    data: ArrayBuffer;
    mimetype: string;
    name: string;
  } | null> = new Source(null);

  async function doSend() {
    const title = titleS.get();
    const html = htmlS.get();
    if (title.trim() === "") {
      alert("Vul het onderwerp in");
      return;
    }
    if (html.trim() === "") {
      alert("Vul je email in");
      return;
    }
    const attachmentCurr = attachmentS.get();
    const attachment = attachmentCurr
      ? {
          data_b64: arrayBufferToBase64Client(attachmentCurr.data),
          mimetype: attachmentCurr.mimetype,
          name: attachmentCurr.name,
        }
      : null;
    await rpc(routes.sendGroupMessageSpec, undefined, {
      html,
      title,
      attachment,
      target: {
        tag: "teachers",
        teacherids: filteredTeachers
          .filter((s) => s.selectedS.get() === true)
          .map((s) => s.id)
          .uniq(),
      },
    });
  }

  scheduleForCleanup(instrumentsS.observe(recalcTeachers));

  const filteredTeachers = opts.teachers
    .orderBy((t) => t.firstname + " " + t.lastname)
    .map((t) => ({
      ...t,
      selectedS: new Source(true),
    }));

  function recalcTeachers() {
    const instrumentsSelected = instrumentsS.get();
    if (instrumentsSelected.length === 0) {
      filteredTeachers.forEach((t) => t.selectedS.set(true));
    } else {
      filteredTeachers.forEach((t) => {
        if (
          (t.instruments || []).some((tt) =>
            instrumentsSelected.includes(tt.id),
          )
        ) {
          t.selectedS.set(true);
        } else {
          t.selectedS.set(false);
        }
      });
    }
  }

  function renderFilterSidebarTeachers() {
    return (
      <aside>
        <div>
          <b>{"Filters"}</b>
        </div>

        {renderBehindToggle(
          instrumentFilterToggleS,
          "Cursus",
          renderCheckboxes(
            instruments,
            instrumentsS,
            (i) => i.id,
            (i) => i.description,
          ),
        )}
      </aside>
    );
  }

  return dyn(modeS, (mode) => {
    if (mode === "filter") {
      return [
        renderFilterSidebarTeachers(),
        <main>
          <label>{"Verder"}</label>
          <section>
            <button
              style="width: 100%"
              className={style.button_accent}
              onclick={() => modeS.set("message")}
            >
              {"Schrijf bericht"}
              <i
                className={FA.icon_arrow_right}
                style="margin-left: 24px; margin-right: 0px"
              ></i>
            </button>
          </section>

          <label>{"Docenten"}</label>
          <section className={style.panel}>
            {filteredTeachers.map((t) => (
              <div>
                {checkbox({
                  source: t.selectedS,
                  label: t.firstname + " " + t.lastname,
                })}
              </div>
            ))}
          </section>
        </main>,
      ];
    } else if (mode === "message") {
      const editor = renderEditor(htmlS, null, null);
      return (
        <main>
          <section
            className={style.card + style.card_large}
            style="width: 100%; display: flex; justify-content: space-between"
          >
            <div style="margin-right: 32px">
              {"Je hebt %i docenten geselecteerd die je bericht zullen krijgen.".replace(
                "%i",
                filteredTeachers
                  .filter((s) => s.selectedS.get() === true)
                  .length.toString(),
              )}
            </div>
            <div style="">
              <button onclick={() => modeS.set("filter")}>
                {"Bekijk ze hier"}
              </button>
            </div>
          </section>

          <div className={style.card + style.card_large}>
            {textbox({ source: titleS, label: "Onderwerp" })}

            <label>{"Bericht"}</label>
            {editor.buttonSection}
            {editor.editablediv}
            <label for="">
              {"Bijlage"}
              <div className={style.input_with_icon}>
                <i className={FA.icon_paperclip_solid}></i>
                {renderFileInput({
                  source: attachmentS,
                  initiallabel: "Voeg bijlage toe",
                  selectedlabel: "Bijlage toegevoegd",
                  className: style.input_like,
                  style: "cursor: pointer",
                })}
              </div>
            </label>
            <div style="text-align: right">
              {renderSaveButton({
                save: doSend,
                text: {
                  normal: "Verzend berichten",
                  success: "Berichten verzonden!",
                },
                reload: false,
                timeout: 60000,
              })}
            </div>
          </div>
        </main>
      );
    } else {
      return checkAllCasesHandled(mode);
    }
  });
}

function renderRadioCheckboxes<Item, Id extends string | number | boolean>(
  items: Item[],
  s: Source<Id>,
  toId: (i: Item) => Id,
  show: (i: Item) => string,
) {
  return (
    <div>
      {items.map((item) => {
        return (
          <label className={style.checkbox}>
            {dyn(s, (curr) => (
              <input
                type="checkbox"
                // eslint-disable-next-line
                checked={curr === toId(item) ? "true" : null}
                onclick={() => s.set(toId(item))}
              />
            ))}
            {show(item)}
          </label>
        );
      })}
    </div>
  );
}

function renderRadioRecap<Item, Id extends string | number | boolean>(
  items: Item[],
  s: Source<Id>,
  toId: (i: Item) => Id,
  show: (i: Item) => string,
  opts: {
    noFilterValue: Id;
    title: string;
  },
) {
  return dyn(s, (curr) => {
    // eslint-disable-next-line
    const currItem = items.find((i) => toId(i) === curr);
    // eslint-disable-next-line
    return curr === opts.noFilterValue ? (
      <span style="display: none;"></span>
    ) : (
      <div className={style.card} style={recapStyle}>
        <div>
          <label>{opts.title}</label>
          <div>{currItem !== undefined ? show(currItem) : ""}</div>
        </div>
      </div>
    );
  });
}

function enrollmentIsActive(
  today: joda.LocalDate,
  s: {
    enrollment_status: enrollment_status;
    lessongroup_lastlesson: null | joda.LocalDate;
    enrollment_stoppedlessonson: null | joda.LocalDate;
  },
): boolean {
  return !!match(s)
    .with(
      { enrollment_status: "ActiveEnrollment" },
      (s) =>
        s.lessongroup_lastlesson &&
        (s.lessongroup_lastlesson.isEqual(today) ||
          s.lessongroup_lastlesson.isAfter(today)),
    )
    .with(
      { enrollment_status: "TrialLesson" },
      (s) =>
        s.enrollment_stoppedlessonson &&
        s.enrollment_stoppedlessonson.isAfter(today),
    )
    .with({ enrollment_status: "Stopped" }, () => false)
    .exhaustive();
}

function renderWithActiveEnrollment(i: with_active_enrollment) {
  return i === "no_filter"
    ? "Geen filter"
    : i === "with_active_enrollment"
    ? "Volgt op dit moment lessen"
    : checkAllCasesHandled(i);
}
